/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ChangeEvent, RefObject } from 'react';
import { useState } from 'react';

import { Button, Dialog, PasswordField } from '@coursera/cds-core';

import { cancelTwoFactorAuthenticationAction } from 'bundles/account-settings/actions/SettingsActionCreators';

import _t from 'i18n!nls/account-settings';

type Props = {
  title: string;
  invalidFields: string[];
  updating: boolean;
  submitAction: (password: string) => void;
  onClose?: () => void;
  returnFocusRef?: RefObject<HTMLElement>;
};

const styles = {
  actions: css`
    display: flex;
    gap: var(--cds-spacing-100);
    justify-content: flex-start;
  `,
};

const PasswordConfirmationModal = ({
  title,
  invalidFields,
  updating,
  submitAction,
  onClose,
  returnFocusRef,
}: Props) => {
  const [password, setPassword] = useState('');
  const hasPasswordError = invalidFields.includes('password');

  const handleSubmit = () => {
    submitAction(password);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value.trim());
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      cancelTwoFactorAuthenticationAction();
    }
  };

  return (
    <Dialog open onClose={handleClose} returnFocusRef={returnFocusRef}>
      <Dialog.HeadingGroup>{title}</Dialog.HeadingGroup>
      <Dialog.Content>
        <PasswordField
          label={_t('Confirm your password')}
          placeholder={_t('Enter your password')}
          autoComplete="current-password"
          onChange={handleChange}
          fullWidth
          validationStatus={hasPasswordError ? 'error' : undefined}
          validationLabel={hasPasswordError ? _t('Please confirm your password') : undefined}
        />
      </Dialog.Content>
      <Dialog.Actions css={styles.actions}>
        <Button variant="primary" onClick={handleSubmit} disabled={updating} type="button">
          {_t('Continue')}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export default PasswordConfirmationModal;
